import { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import "moment/locale/es";
import BarraFecha from "../Components/Citas/BarraFecha";
import AgendaCuadricula from "../Components/Citas/AgendaCuadricula";
import { ObtenerMiAgenda } from "../Service/Service.citas";
import { tituloClinico, separarEspecialidad } from "../utils/textoClinico";
import "../styles/CitasMiAgenda.css";

moment.locale("es");

/* ============================================================================
   MI AGENDA · la vista del médico

   Sustituye a la maqueta que ocupaba esta ruta: un FullCalendar con un evento
   "Meeting" inventado y manipulación directa del DOM. Era plantilla, no
   producto, y estaba accesible con sólo escribir la URL.

   Qué la diferencia de /gestion_citas:

   · NO hay rail de especialidades. El médico no elige especialidad, ya sabe
     cuál es la suya -- va en el título. Sólo si tiene DOS aparece un selector,
     porque entonces sí hay algo que elegir (y cada una puede tener su propio
     intervalo de cupo).
   · Una sola columna: la suya. El `iddoctor` no se manda, lo saca el backend
     del token -- no hay forma de pedir la agenda de otro.
   · No AGENDA: un cupo libre no ofrece "Agendar" porque esta pantalla no hace
     eso. Pero sus citas SÍ son accionables -- lo que un médico quiere de su
     agenda es entrar a atender. Pulsar una cita, con el botón que sea, abre el
     menú de acciones; no lleva a ningún sitio de golpe. Así hay UN solo gesto
     que aprender, y sitio donde añadir lo que haga falta después.

   Reutiliza la barra de fecha y la cuadrícula de /gestion_citas tal cual: los
   estados, los colores y el comportamiento son los mismos que ve recepción.
   ========================================================================== */

/* Menú contextual propio, en vez del de la agenda general: aquel posiciona con
   desplazamientos fijos (`position.y - 250`, `position.x - 580`) calculados
   para aquella pantalla, y aquí caería fuera de sitio. Éste se abre donde se
   hizo clic y se recoge si no cabe. */
function MenuCita({ x, y, opciones, onCerrar }) {
    const ref = useRef(null);

    useEffect(() => {
        const alPulsarTecla = (e) => e.key === "Escape" && onCerrar();
        const alPulsarFuera = (e) => {
            if (ref.current && !ref.current.contains(e.target)) onCerrar();
        };
        document.addEventListener("keydown", alPulsarTecla);
        document.addEventListener("mousedown", alPulsarFuera);
        return () => {
            document.removeEventListener("keydown", alPulsarTecla);
            document.removeEventListener("mousedown", alPulsarFuera);
        };
    }, [onCerrar]);

    const ANCHO = 190;
    const alto = opciones.length * 38 + 8;
    const izq = Math.min(x, window.innerWidth - ANCHO - 8);
    const arriba = Math.min(y, window.innerHeight - alto - 8);

    return (
        <div
            ref={ref}
            className="miagenda__menu"
            role="menu"
            style={{ left: izq, top: arriba, width: ANCHO }}
        >
            {opciones.map((o) => (
                <button
                    key={o.label}
                    type="button"
                    role="menuitem"
                    className="miagenda__menu-item"
                    onClick={() => {
                        o.action();
                        onCerrar();
                    }}
                >
                    {o.label}
                </button>
            ))}
        </div>
    );
}

function Aviso({ titulo, texto }) {
    return (
        <div className="miagenda__aviso">
            <p className="miagenda__aviso-titulo">{titulo}</p>
            <p className="miagenda__aviso-texto">{texto}</p>
        </div>
    );
}

/** Mensajes de los casos en que un usuario no tiene agenda propia. Cada uno
 *  dice qué pasa y qué hacer: "no autorizado" a secas no ayuda a nadie. */
const AVISOS = {
    SIN_FICHA_MEDICO: {
        titulo: "Esta pantalla es para el personal médico",
        texto:
            "Tu usuario no está vinculado a una ficha de médico activa. Si eres médico y deberías ver tu agenda aquí, avisa a sistemas para que revisen la vinculación de tu ficha.",
    },
    SIN_ESPECIALIDAD_ASIGNADA: {
        titulo: "Tu ficha no tiene especialidad asignada",
        texto:
            "Sin especialidad no se puede calcular el horario de los cupos. Pídele a administración que la asigne en tu ficha de médico.",
    },
    FUERA_DE_ALCANCE: {
        titulo: "No se encontró tu agenda",
        texto: "Tu ficha de médico no pertenece a ninguna de las clínicas a las que tienes acceso.",
    },
};

export default function MiAgenda() {
    const [fecha, setFecha] = useState(moment().format("YYYY-MM-DD"));
    // Sólo lo que el médico ha ELEGIDO, y null mientras no elija nada. No se
    // rellena con la especialidad que devuelve el backend: hacerlo cambiaría
    // la identidad de `cargar` justo después de cargar, y la página pediría su
    // agenda dos veces en cada visita.
    const [idespecialidad, setIdEspecialidad] = useState(null);
    const [estadosVisibles, setEstadosVisibles] = useState(null);

    const [menu, setMenu] = useState(null);
    const [datos, setDatos] = useState(null);
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState(null);
    const [aviso, setAviso] = useState(null);

    const cargar = useCallback(async () => {
        setCargando(true);
        setError(null);
        const respuesta = await ObtenerMiAgenda(fecha, idespecialidad);

        if (respuesta?.success) {
            setDatos(respuesta.data);
            setAviso(null);
        } else if (respuesta?.codigo && AVISOS[respuesta.codigo]) {
            setAviso(AVISOS[respuesta.codigo]);
            setDatos(null);
        } else {
            setError(respuesta?.message || "No se pudo cargar tu agenda");
        }
        setCargando(false);
    }, [fecha, idespecialidad]);

    useEffect(() => {
        cargar();
    }, [cargar]);

    // La misma dirección que abre la agenda general desde "Abrir Consulta"
    // (ver AgendaContenedor.js): una sola forma de entrar a atender.
    const abrirConsulta = useCallback((cita) => {
        const idpaciente = cita?.pacientes?.idpaciente;
        if (!idpaciente) return;
        window.open(`/paciente_historial?idpaciente=${idpaciente}&idcita=${cita.idcita}`);
    }, []);

    /** Las acciones de una cita, donde se pulsó. */
    const menuDeCita = useCallback(
        (cita, e) => {
            e.preventDefault();
            setMenu({
                x: e.clientX,
                y: e.clientY,
                opciones: [{ label: "Abrir consulta", action: () => abrirConsulta(cita) }],
            });
        },
        [abrirConsulta]
    );

    // Clic derecho sobre una celda. Sobre un cupo libre no hay nada que
    // ofrecer: esta pantalla no agenda.
    const menuDeCelda = useCallback(
        (e, doctor, hora) => {
            const cita = (datos?.citas ?? []).find(
                (c) =>
                    String(c.hora_inicio).padStart(5, "0") === hora &&
                    moment(c.fecha_inicio).format("YYYY-MM-DD") === fecha
            );
            if (!cita) return;
            menuDeCita(cita, e);
        },
        [datos, fecha, menuDeCita]
    );

    const especialidades = datos?.especialidades ?? [];
    const activa = especialidades.find((e) => e.idespecialidad === datos?.idespecialidadActiva);
    const nombre = tituloClinico(datos?.doctor?.nombre ?? "");

    return (
        <div className="page-content">
            <div className="container-fluid">
                <div className="miagenda__cabecera">
                    <div>
                        <h4 className="miagenda__titulo">{nombre || "Mi agenda"}</h4>
                        {especialidades.length > 0 && (
                            <p className="miagenda__subtitulo">
                                {especialidades
                                    .map((e) => separarEspecialidad(e.especialidad).nombre)
                                    .join(" · ")}
                            </p>
                        )}
                    </div>

                    {/* El selector aparece SÓLO con dos o más especialidades.
                        Con una, elegir no significa nada y el título ya lo dice. */}
                    {especialidades.length > 1 && (
                        <div className="miagenda__especialidades" role="group" aria-label="Elegir especialidad">
                            {especialidades.map((e) => {
                                const esActiva = e.idespecialidad === activa?.idespecialidad;
                                return (
                                    <button
                                        key={e.idespecialidad}
                                        type="button"
                                        className={`miagenda__esp${esActiva ? " miagenda__esp--activa" : ""}`}
                                        onClick={() => setIdEspecialidad(e.idespecialidad)}
                                        aria-pressed={esActiva}
                                    >
                                        {separarEspecialidad(e.especialidad).nombre}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {aviso ? (
                    <Aviso titulo={aviso.titulo} texto={aviso.texto} />
                ) : (
                    <>
                        <BarraFecha
                            fecha={fecha}
                            onCambiarFecha={setFecha}
                            estadosVisibles={estadosVisibles}
                            onCambiarEstados={setEstadosVisibles}
                        />

                        <AgendaCuadricula
                            doctores={datos?.agenda ?? []}
                            citas={datos?.citas ?? []}
                            fecha={fecha}
                            cargando={cargando}
                            error={error}
                            estadosVisibles={estadosVisibles}
                            soloLectura
                            onCitaPulsada={menuDeCita}
                            onMenuContextual={menuDeCelda}
                            onReintentar={cargar}
                            onIrDiaSiguiente={() =>
                                setFecha(moment(fecha).add(1, "days").format("YYYY-MM-DD"))
                            }
                        />
                    </>
                )}

                {menu && (
                    <MenuCita
                        x={menu.x}
                        y={menu.y}
                        opciones={menu.opciones}
                        onCerrar={() => setMenu(null)}
                    />
                )}
            </div>
        </div>
    );
}
